Ext.define('SA.view.scatterplot.ScatterPlot', {
    extend: 'Ext.Component',
    alias: 'widget.scatter-plot',

    html: [
        '<svg>',
            '<rect fill="#fff" width="100%" height="100%"></rect>',
            '<g class="wrap">',
                '<g class="title"></g>',
                '<g class="body">',
                    '<g class="chart">',
                        '<g class="vertical-grid-lines"></g>',
                        '<g class="horizontal-grid-lines"></g>',
                        '<g class="axis-x"></g>',
                        '<g class="axis-y"></g>',
                        '<g class="points"></g>',
                        '<g class="line"></g>',
                    '</g>',
                    '<g class="legend"></g>',
                '</g>',
            '</g>',
        '</svg>'
    ],

    statics: {
        calculateCorrelation(data, xAttribute, yAttribute) {
            var sq = n => n ** 2,
                sqrt = Math.sqrt,
                n = data.length,
                sumXY = 0,
                sumX = 0,
                sumY = 0,
                sumXSquared = 0,
                sumYSquared = 0;

            data.forEach(({[xAttribute]: x, [yAttribute]: y}) => {
                sumXY += x * y;
                sumX += x;
                sumY += y;
                sumXSquared += sq(x);
                sumYSquared += sq(y);
            });

            return (n * sumXY - sumX * sumY) / (sqrt(n * sumXSquared - sq(sumX)) * sqrt(n * sumYSquared - sq(sumY)));
        },

        formatCorrelation(correlation) {
            return Ext.isNumber(correlation) ? `r = ${correlation.toFixed(3)}` : 'N/A';
        }
    },

    afterRender() {
        this.callParent();

        this.points = new SA.view.scatterplot.ScatterPlotPointsGroup({parent: this.getEl().dom.querySelector('.points')});
    },

    getPointCount() {
        return this.points.getPointCount();
    },

    getSvg() {
        return this.el.down('svg', true).cloneNode(true);
    },

    renderChart({cfg, data, geographicUnit, isExport, isLineOfBestFitEnabled, location, pointColor, pointRadius, title, titleWidth, xAttribute, yAttribute}) {
        pointColor = '#' + pointColor;

        var me = this,
            el = this.el.dom,
            svg = d3.select(el.querySelector('svg')),
            pointStroke = Ext.draw.Color.fromString(pointColor).getDarker(cfg.pointStrokeDarkerFactor).toString(),
            yScale = getYScale(),
            xScale = getXScale();

        renderTitle();
        renderGrid();
        renderYAxis();
        renderXAxis();
        renderLineOfBestFit();
        renderLegend();

        this.points.render({
            data,
            onClick: onClickPoint,
            onMouseOut: onMouseOutPoint,
            onMouseOver: onMouseOverPoint,
            pointColor,
            pointRadius,
            pointStroke,
            pointStrokeWidth: cfg.pointStrokeWidth,
            xAttribute,
            xScale,
            yAttribute,
            yScale
        });

        setBodyOffset();
        setLegendOffset();
        setWrapOffset();
        setSize();

        function renderTitle() {
            svg.select('.title').selectAll('*').remove();

            if (!isExport) {
                return;
            }

            d3.select(SA.SvgTextRenderer.renderWidget(svg.select('.title').node(), {
                xtype: 'component',
                html: Ext.String.htmlEncode(title),

                style: {
                    color: cfg.titleFontColor,
                    fontFamily: cfg.fontFamily,
                    fontSize: `${cfg.titleFontSize}px`,
                    fontVariant: cfg.fontVariant,
                    letterSpacing: `${cfg.letterSpacing}px`,
                    lineHeight: cfg.lineHeight,
                    maxWidth: `${titleWidth}px`,
                    textAlign: 'center'
                }
            }))
            .attr('transform', (_, i, els) => `translate(${(cfg.xAxisWidth - els[i].getBBox().width) / 2})`);
        }

        function renderGrid() {
            var verticalGridLines = svg.select('.vertical-grid-lines'),
                horizontalGridLines = svg.select('.horizontal-grid-lines'),
                yAxis = d3.axisLeft(yScale).ticks(cfg.yAxisTickCount).tickSize(-cfg.xAxisWidth),
                xAxis = d3.axisBottom(xScale).ticks(cfg.xAxisTickCount).tickSize(cfg.yAxisHeight),
                strokeColor = cfg.gridLineColor;

            verticalGridLines.selectAll('*').remove();
            horizontalGridLines.selectAll('*').remove();

            horizontalGridLines.call(yAxis);
            horizontalGridLines.select('.domain').remove();
            horizontalGridLines.selectAll('text').remove();
            horizontalGridLines.selectAll('line').attr('stroke', strokeColor);

            verticalGridLines.call(xAxis);
            verticalGridLines.select('.domain').remove();
            verticalGridLines.selectAll('text').remove();
            verticalGridLines.selectAll('line').attr('stroke', strokeColor);
        }

        function renderYAxis() {
            var axis = svg.select('.axis-y');

            SA.view.scatterplot.YAxisRenderer.render(axis, cfg, yScale, yAttribute);
        }

        function renderXAxis() {
            var axis = svg.select('.axis-x');

            SA.view.scatterplot.XAxisRenderer.render(axis, cfg, xScale, xAttribute);
        }

        function renderLineOfBestFit() {
            svg.select('.line').selectAll('*').remove();

            if (!isLineOfBestFitEnabled || data.length < 2) {
                return;
            }

            var xs = data.map(d => xScale(d[xAttribute])),
                ys = data.map(d => yScale(d[yAttribute])),
                areXsConstant = new Set(xs).size === 1,
                areYsConstant = new Set(ys).size === 1;

            if (areXsConstant && areYsConstant) {
                return;
            }

            if (areYsConstant) {
                renderLine(0, ys[0], cfg.xAxisWidth, ys[0]);
            }
            else if (areXsConstant) {
                renderLine(xs[0], 0, xs[0], cfg.yAxisHeight);
            }
            else {
                let sum = array => Ext.Array.sum(array),
                    square = n => n ** 2,
                    xys = data.map(d => xScale(d[xAttribute]) * yScale(d[yAttribute])),
                    slope = (data.length * sum(xys) - sum(xs) * sum(ys)) / (data.length * sum(xs.map(square)) - square(sum(xs))),
                    yIntercept = (sum(ys) - slope * sum(xs)) / data.length,
                    f = x => slope * x + yIntercept,
                    fInverse = y => (y - yIntercept) / slope,
                    xLeft = Math.max(0, Math.min(fInverse(0), fInverse(cfg.yAxisHeight))),
                    xRight = Math.min(cfg.xAxisWidth, Math.max(fInverse(0), fInverse(cfg.yAxisHeight)));

                renderLine(xLeft, f(xLeft), xRight, f(xRight));
            }

            function renderLine(x1, y1, x2, y2) {
                svg.select('.line')
                    .append('line')
                    .attr('x1', x1).attr('y1', y1)
                    .attr('x2', x2).attr('y2', y2)
                    .attr('stroke', cfg.lineOfBestFitStroke)
                    .attr('stroke-dasharray', cfg.lineOfBestFitDashArray)
                    .attr('stroke-width', cfg.lineOfBestFitStrokeWidth);
            }
        }

        function renderLegend() {
            var legend = svg.select('.legend');

            legend.selectAll('*').remove();

            if (!isExport) {
                return;
            }

            legend.call(renderPointsRepresent).call(renderCorrelation);

            function renderPointsRepresent(legend) {
                legend.call(renderSymbol).call(renderText);

                function renderSymbol(legend) {
                    legend.append('circle')
                        .attr('cx', cfg.legendSymbolColumnWidth / 2)
                        .attr('cy', cfg.legendFontSize / 2)
                        .attr('fill', pointColor)
                        .attr('r', pointRadius)
                        .attr('stroke', pointStroke)
                        .attr('stroke-width', cfg.pointStrokeWidth);
                }

                function renderText(legend) {
                    SA.SvgTextRenderer.renderWidget(legend.node(), {
                        xtype: 'component',
                        style: cfg.getLegendTextStyle(),
                        tpl: '{geographicUnit.name:htmlEncode} in {location:locationRenderer}',

                        data: {
                            geographicUnit: SA.data.GeographicUnits.get(geographicUnit),
                            location: SA.data.LocationsCache.get(location)
                        }
                    }, {left: cfg.legendSymbolColumnWidth});
                }
            }

            function renderCorrelation(legend) {
                var {calculateCorrelation, formatCorrelation} = SA.view.scatterplot.ScatterPlot,
                    top = legend.node().getBBox().height + cfg.legendSectionSpacing,
                    correlation = calculateCorrelation(data, xAttribute, yAttribute);

                SA.SvgTextRenderer.renderWidget(legend.node(), {
                    xtype: 'component',
                    data: formatCorrelation(correlation),
                    style: cfg.getLegendTextStyle(),
                    tpl: 'Correlation: {.:htmlEncode}'
                }, {left: cfg.legendSymbolColumnWidth, top});
            }
        }

        function getYScale() {
            var values = data.map(d => d[yAttribute]);

            return getScale(values, cfg.yAxisHeight, 0);
        }

        function getXScale() {
            var values = data.map(d => d[xAttribute]);

            return getScale(values, 0, cfg.xAxisWidth);
        }

        function getScale(values, rangeMin, rangeMax) {
            var domainMin = d3.min(values),
                domainMax = d3.max(values);

            return d3.scaleLinear()
                .domain([domainMin, domainMax])
                .rangeRound([rangeMin, rangeMax])
                .nice();
        }

        function getPointRadius(point) {
            if (point.classList.contains('sa-scatter-plot-point-selected')) {
                return pointRadius + cfg.pointSelectedRadiusIncrease;
            }
            else if (point.classList.contains('sa-scatter-plot-point-hovered')) {
                return pointRadius + cfg.pointHoveredRadiusIncrease;
            }

            return pointRadius;
        }

        function onMouseOverPoint(data, point) {
            point.classList.add('sa-scatter-plot-point-hovered');

            point.setAttributeNS(null, 'r', getPointRadius(point));

            me.fireEvent('mouseover:point', data, point);
        }

        function onMouseOutPoint(data, point) {
            point.classList.remove('sa-scatter-plot-point-hovered');

            point.setAttributeNS(null, 'r', getPointRadius(point));

            me.fireEvent('mouseout:point', data, point);
        }

        function onClickPoint(data, point) {
            if (point.classList.contains('sa-scatter-plot-point-selected')) {
                deselect();

                me.fireEvent('deselect:point', data, point);
            }
            else {
                point.classList.add('sa-scatter-plot-point-selected');

                point.setAttributeNS(null, 'r', getPointRadius(point));

                me.fireEvent('select:point', data, point, deselect);
            }

            function deselect() {
                point.classList.remove('sa-scatter-plot-point-selected');

                point.setAttributeNS(null, 'r', getPointRadius(point));
            }
        }

        function setBodyOffset() {
            var body = el.querySelector('.body'),
                titleHeight = el.querySelector('.title').getBBox().height;

            if (isExport) {
                body.setAttributeNS(null, 'transform', `translate(0 ${titleHeight + cfg.titleMarginBottom})`);
            }
        }

        function setLegendOffset() {
            var legend = el.querySelector('.legend'),
                x = cfg.xAxisWidth + cfg.legendMarginLeft;

            legend.setAttributeNS(null, 'transform', `translate(${x})`);
        }

        function setWrapOffset() {
            var wrap = el.querySelector('.wrap'),
                chartWidth = el.querySelector('.chart').getBBox().width,
                x = cfg.marginLeft + chartWidth - cfg.xAxisWidth,
                y = cfg.marginTop;

            wrap.setAttributeNS(null, 'transform', `translate(${x} ${y})`);
        }

        function setSize() {
            var box = el.querySelector('.wrap').getBBox(),
                width = box.width + cfg.marginLeft + cfg.marginRight,
                height = box.height + cfg.marginTop + cfg.marginBottom;

            me.setSize(width, height);

            svg.attr('width', width).attr('height', height);
        }
    }
});